// AgentPicker — reusable modal for picking one or more team members.
// Used by the DetailPanel (single design assignment) and the bulk toolbar
// (multi-design assignment). Caller decides whether single or multi.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, UserPlus } from 'lucide-react';
import { listAgents, type Agent } from '../lib/api';

interface AgentPickerProps {
  open: boolean;
  // Already-selected agent ids (so checkboxes start checked).
  initialSelectedIds?: string[];
  // Header text. Caller controls based on context.
  title: string;
  subtitle?: string;
  // Confirm button label, e.g. "Assign 3 designs".
  confirmLabel: string;
  onConfirm: (agentIds: string[]) => void;
  onClose: () => void;
  busy?: boolean;
}

export function AgentPicker({
  open,
  initialSelectedIds = [],
  title,
  subtitle,
  confirmLabel,
  onConfirm,
  onClose,
  busy,
}: AgentPickerProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelectedIds));
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['agents', { archived: false }],
    queryFn: () => listAgents({ archived: false }),
    enabled: open,
  });

  if (!open) return null;

  const agents = query.data?.agents ?? [];
  const filtered = search.trim()
    ? agents.filter((a) =>
        `${a.first_name} ${a.last_name ?? ''} ${a.title ?? ''} ${a.email ?? ''}`
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      )
    : agents;

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal--lg" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <div>
            <h3>{title}</h3>
            {subtitle && <p className="modal__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <input
          type="search"
          className="agent-picker__search"
          placeholder="Search team members…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        {query.isLoading ? (
          <div className="state">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="state">
            {agents.length === 0 ? (
              <p>No team members yet. Add one from the My Team page first.</p>
            ) : (
              <p>No matches for &ldquo;{search}&rdquo;.</p>
            )}
          </div>
        ) : (
          <ul className="agent-picker__list">
            {filtered.map((a) => (
              <AgentRow
                key={a.id}
                agent={a}
                checked={selected.has(a.id)}
                onToggle={() => toggle(a.id)}
              />
            ))}
          </ul>
        )}

        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--hero"
            disabled={selected.size === 0 || busy}
            onClick={() => onConfirm(Array.from(selected))}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

function AgentRow({
  agent,
  checked,
  onToggle,
}: {
  agent: Agent;
  checked: boolean;
  onToggle: () => void;
}) {
  const fullName = [agent.first_name, agent.last_name].filter(Boolean).join(' ');
  return (
    <li className={`agent-picker__row${checked ? ' is-checked' : ''}`} onClick={onToggle}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Select ${fullName}`}
      />
      {agent.headshot_url ? (
        <img src={agent.headshot_url} alt="" className="agent-picker__avatar" />
      ) : (
        <span className="agent-picker__avatar agent-picker__avatar--placeholder" aria-hidden="true">
          {agent.first_name.charAt(0).toUpperCase()}
        </span>
      )}
      <div className="agent-picker__info">
        <div className="agent-picker__name">
          {fullName}
          {agent.is_self && <span className="pill pill--gold">You</span>}
        </div>
        {agent.title && <div className="agent-picker__title">{agent.title}</div>}
      </div>
    </li>
  );
}

export function AgentPickerEmpty({ onAddAgent }: { onAddAgent: () => void }) {
  return (
    <div className="agent-picker__empty">
      <UserPlus size={28} aria-hidden="true" strokeWidth={1.4} />
      <p>You haven&rsquo;t added any team members yet.</p>
      <button type="button" className="btn btn--primary" onClick={onAddAgent}>
        Go to My Team
      </button>
    </div>
  );
}
